import './styles.scss'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputNumber } from 'antd'
import { clsx } from 'clsx'

import { MinusIcon, PlusIcon } from '@/components/Icons'
import usePermission from '@/hooks/usePermission'
import { MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'
import { formatQuantity } from '@/libs/utils/handleNumber'

import CustomButton from '../CustomButton'
import QUANTITY_TYPE from './constants'

interface B2becQuantityProps {
  materialNumber?: string | object
  initialQuantity?: number
  minIntDigits?: number
  onChange?: (materialNumber: string | object, newQuantity: number) => void
  type?: number
  wrapperClassName?: string
  buttonClassName?: string
  inputClassName?: string
  isPriceEqualZero?: boolean
  isParentDisabled?: boolean
}

const B2becQuantity = ({
  materialNumber = '',
  initialQuantity = 1,
  minIntDigits = 2,
  onChange,
  type = QUANTITY_TYPE.PRODUCT_DETAIL,
  wrapperClassName,
  buttonClassName,
  inputClassName,
  isPriceEqualZero = false,
  isParentDisabled = false,
}: B2becQuantityProps) => {
  const { t } = useTranslation()
  const [errorMessage, setErrorMessage] = useState('')
  const [quantityValue, setQuantityValue] = useState(initialQuantity)
  const { hasPermission: hasChangeCartPermission } = usePermission(
    MODULE_SHOP_NAVIGATION_RIGHTS.CHANGE_CART,
  )

  const isDisable = !hasChangeCartPermission
    || isParentDisabled
    || isPriceEqualZero
    || (type === QUANTITY_TYPE.SHOPPING_CART
      && typeof materialNumber !== 'string')

  const handleChangeQuantity = (newQuantity) => {
    const isValidQuantity = /^\d+$/.test(newQuantity)

    if (isValidQuantity) {
      setErrorMessage(null)
      setQuantityValue(newQuantity)

      if (type === QUANTITY_TYPE.SHOPPING_CART) {
        onChange?.(materialNumber, newQuantity)
      } else {
        onChange?.(newQuantity)
      }
    } else {
      setErrorMessage('errors.positiveNumber')
    }
  }

  const onIncrease = () => {
    setQuantityValue(quantityValue + 1)
    handleChangeQuantity(quantityValue + 1)
  }
  const onDecrease = () => {
    setQuantityValue(quantityValue - 1)
    handleChangeQuantity(quantityValue - 1)
  }

  useEffect(() => {
    setQuantityValue(initialQuantity)
  }, [initialQuantity])

  return (
    <div className={clsx('quantity-wrapper', wrapperClassName)}>
      <div className="quantity__top">
        <CustomButton
          isQuantity
          aria-label="decrease-button"
          className={buttonClassName}
          disabled={isDisable || quantityValue <= 1 || !hasChangeCartPermission}
          onClick={onDecrease}
        >
          <MinusIcon />
        </CustomButton>

        <InputNumber
          className={`quantity__top__input ${inputClassName}`}
          disabled={isDisable}
          formatter={(value) => formatQuantity(value, minIntDigits)}
          type="number"
          value={formatQuantity(quantityValue || 1, minIntDigits)}
          onFocus={(event) => event.target.select()}
          onBlur={() => {
            setErrorMessage('')
          }}
          onChange={(value) => {
            if (quantityValue !== value) {
              handleChangeQuantity(value)
            }
          }}
        />
        {errorMessage
          ? (
            <p className="custom-validate-input__wrapper--error__message quantity__top__input--error">
              {t(errorMessage)}
            </p>
          )
          : null}

        <CustomButton
          isQuantity
          aria-label="increase-button"
          className={buttonClassName}
          disabled={isDisable || !hasChangeCartPermission}
          onClick={onIncrease}
        >
          <PlusIcon />
        </CustomButton>
      </div>
    </div>
  )
}

export default B2becQuantity
